import { useEffect, useRef } from "react";
import useEndOfServiceConfiguration from "./hooks/useEndOfServiceConfiguration";
import { useGetEndOfServiceTime } from "./hooks/useGetEndOfServiceTime";
import { useEndOfService } from "./EndOfServiceProvider";
import { useTranslation } from "@src/hooks/useTranslation";
import useAppModelChange from "@src/backbone/hooks/useAppModelChange";
import useCartModelChange from "@src/backbone/hooks/useCartModelChange";
import moment from "moment";

// Clés pour le localStorage
const FIRST_WARNING_DISMISSED_KEY = "end_of_service_first_warning_dismissed";
const SECOND_WARNING_DISMISSED_KEY = "end_of_service_second_warning_dismissed";
const THIRD_WARNING_DISMISSED_KEY = "end_of_service_third_warning_dismissed";
const END_OF_SERVICE_ALERT_DISMISSED_KEY = "end_of_service_alert_dismissed";
const id_first_warning_notice = "end_of_service_first_warning_notice";
const id_second_warning_notice = "end_of_service_second_warning_notice";
const id_third_warning_notice = "end_of_service_third_warning_notice";
const id_end_of_service_alert_notice = "end_of_service_alert_notice";

export const useEndOfServiceLogic = () => {
  const {
    timeToClosingTime,
    store,
    storeId,
    getRemainingTimeOfEndOfService,
    getNextOpeningTime,
  } = useGetEndOfServiceTime();
  const [, app] = useAppModelChange(["cart"]);
  const [, cart] = useCartModelChange(["id_store", "products"]);
  const { firstWarningDelay, secondWarningDelay, thirdWarningDelay } =
    useEndOfServiceConfiguration();
  const { addNotice, notices } = useEndOfService();
  const { t } = useTranslation(["Warning"]);

  // Références pour suivre si les notifications ont été affichées dans cette session
  // et pour stocker les identifiants des setTimeout
  const firstWarningShownRef = useRef(false);
  const secondWarningShownRef = useRef(false);
  const thirdWarningShownRef = useRef(false);
  const endOfServiceAlertShownRef = useRef(false);
  const firstTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const secondTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const thirdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const endOfServiceAlertTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dismissedNotices = useRef<{
    [key: string]: string[];
  }>({});
  // go to cart
  const goToCart = () => {
    // check if cart is empty
    if (cart && cart.hasItems()) {
      app.go("cart");
    } else {
      app.go("listing");
    }
  };

  // Fonction pour vérifier si une notification a été fermée manuellement
  const isNoticeDismissed = (key: string, storeId?: string): boolean => {
    if (!storeId) return false;

    try {
      return dismissedNotices.current?.[storeId]?.includes(key) ?? false;
    } catch (error) {
      console.error("Error checking dismissed notice:", error);
      return false;
    }
  };

  // Fonction pour marquer une notification comme fermée
  const markNoticeAsDismissed = (key: string, storeId?: string) => {
    if (!storeId) return;

    try {
      dismissedNotices.current = dismissedNotices.current || {};
      dismissedNotices.current[storeId] =
        dismissedNotices.current[storeId] || [];
      dismissedNotices.current[storeId].push(key);
      localStorage.setItem(key, JSON.stringify(dismissedNotices.current));
    } catch (error) {
      console.error("Error marking notice as dismissed:", error);
    }
  };

  // Fonction pour afficher la première notification
  const showFirstWarning = (storeId: string) => {
    const remainingTime = getRemainingTimeOfEndOfService();
    if (
      remainingTime &&
      remainingTime > 0 &&
      !firstWarningShownRef.current &&
      !isNoticeDismissed(FIRST_WARNING_DISMISSED_KEY, storeId) &&
      !notices.some((notice) => notice.id === id_first_warning_notice)
    ) {
      addNotice({
        id: id_first_warning_notice,
        title: t("Le shop ferme dans %d minutes !", "Warning", {
          vsprintfArgs: [remainingTime],
        }),
        description: t(
          "Terminez votre commande avant pour garantir votre créneau",
          "Warning"
        ),
        type: "info",
        onClose: () =>
          markNoticeAsDismissed(FIRST_WARNING_DISMISSED_KEY, storeId),
      });
      firstWarningShownRef.current = true;
    }
  };

  // Fonction pour afficher la seconde notification
  const showSecondWarning = (storeId: string) => {
    const remainingTime = getRemainingTimeOfEndOfService();
    if (
      remainingTime &&
      remainingTime > 0 &&
      !secondWarningShownRef.current &&
      !isNoticeDismissed(SECOND_WARNING_DISMISSED_KEY, storeId) &&
      !notices.some((notice) => notice.id === id_second_warning_notice)
    ) {
      addNotice({
        id: id_second_warning_notice,
        title: t("Le shop ferme dans %d minutes !", "Warning", {
          vsprintfArgs: [remainingTime],
        }),
        description: t(
          "Terminez votre commande avant pour garantir votre créneau",
          "Warning"
        ),
        type: "info",
        onClose: () =>
          markNoticeAsDismissed(SECOND_WARNING_DISMISSED_KEY, storeId),
      });
      secondWarningShownRef.current = true;
    }
  };

  // Fonction pour afficher la troisième notification
  const showThirdWarning = (storeId: string) => {
    const remainingTime = getRemainingTimeOfEndOfService();
    if (
      remainingTime &&
      remainingTime > 0 &&
      !thirdWarningShownRef.current &&
      !isNoticeDismissed(THIRD_WARNING_DISMISSED_KEY, storeId) &&
      !notices.some((notice) => notice.id === id_third_warning_notice)
    ) {
      addNotice({
        id: id_third_warning_notice,
        title: t("Le shop ferme dans %d minutes !", "Warning", {
          vsprintfArgs: [remainingTime],
        }),
        description: t(
          "Terminez votre commande avant pour garantir votre créneau",
          "Warning"
        ),
        type: "warning",
        buttonText: t("Finaliser ma commande", "Warning"),
        onButtonClick: () => {
          goToCart();
        },
        onClose: () =>
          markNoticeAsDismissed(THIRD_WARNING_DISMISSED_KEY, storeId),
      });
      thirdWarningShownRef.current = true;
    }
  };

  const showEndOfServiceAlert = () => {
    const nextOpeningTime = getNextOpeningTime();
    if (
      endOfServiceAlertShownRef.current ||
      isNoticeDismissed(END_OF_SERVICE_ALERT_DISMISSED_KEY, storeId) ||
      !nextOpeningTime
    )
      return;

    addNotice({
      id: id_end_of_service_alert_notice,
      title: app.t("Attention", "UI"),
      description: t(
        "Le service est maintenant terminé. Votre commande sera prise pour le prochain créneau disponible, %s",
        "EndOfService",
        {
          vsprintfArgs: [moment(nextOpeningTime).calendar()],
        }
      ),
      type: "modal",
      onClose: () =>
        markNoticeAsDismissed(END_OF_SERVICE_ALERT_DISMISSED_KEY, storeId),
    });

    endOfServiceAlertShownRef.current = true;
  };

  // Effet principal pour gérer l'affichage des notifications et les timeouts
  useEffect(() => {
    // Nettoyez les timeouts précédents lors d'un changement de store ou de temps
    if (firstTimeoutRef.current) {
      clearTimeout(firstTimeoutRef.current);
      firstTimeoutRef.current = null;
    }

    if (secondTimeoutRef.current) {
      clearTimeout(secondTimeoutRef.current);
      secondTimeoutRef.current = null;
    }

    if (thirdTimeoutRef.current) {
      clearTimeout(thirdTimeoutRef.current);
      thirdTimeoutRef.current = null;
    }

    if (endOfServiceAlertTimeoutRef.current) {
      clearTimeout(endOfServiceAlertTimeoutRef.current);
      endOfServiceAlertTimeoutRef.current = null;
    }

    // Réinitialisation des indicateurs à chaque changement de store
    if (storeId) {
      firstWarningShownRef.current = false;
      secondWarningShownRef.current = false;
      thirdWarningShownRef.current = false;
    }

    if (!timeToClosingTime || !storeId || timeToClosingTime <= 0) return;

    // Vérifier immédiatement si les notifications doivent être affichées
    // pour le chargement initial de la page

    // Pour la première notification
    if (
      timeToClosingTime <= firstWarningDelay &&
      timeToClosingTime > secondWarningDelay
    ) {
      showFirstWarning(storeId);
    }

    // Pour la seconde notification
    if (
      timeToClosingTime <= secondWarningDelay &&
      timeToClosingTime > thirdWarningDelay
    ) {
      showSecondWarning(storeId);
    }

    // Pour la troisième notification
    if (timeToClosingTime <= thirdWarningDelay) {
      showThirdWarning(storeId);
    }

    // Programmer des timeouts pour les notifications futures

    // Calcul du délai pour la première notification
    if (timeToClosingTime > firstWarningDelay) {
      const timeToFirstWarning =
        (timeToClosingTime - firstWarningDelay) * 60 * 1000;
      firstTimeoutRef.current = setTimeout(() => {
        showFirstWarning(storeId);
      }, timeToFirstWarning);
    }

    // Calcul du délai pour la seconde notification
    if (timeToClosingTime > secondWarningDelay) {
      const timeToSecondWarning =
        (timeToClosingTime - secondWarningDelay) * 60 * 1000;
      secondTimeoutRef.current = setTimeout(() => {
        showSecondWarning(storeId);
      }, timeToSecondWarning);
    }

    // Calcul du délai pour la troisième notification
    if (timeToClosingTime > thirdWarningDelay) {
      const timeToThirdWarning =
        (timeToClosingTime - thirdWarningDelay) * 60 * 1000;
      thirdTimeoutRef.current = setTimeout(() => {
        showThirdWarning(storeId);
      }, timeToThirdWarning);
    }

    // show end of service alert
    endOfServiceAlertTimeoutRef.current = setTimeout(() => {
      showEndOfServiceAlert();
    }, timeToClosingTime * 60 * 1000);

    // Nettoyage des timeouts lors du démontage ou lorsque les dépendances changent
    return () => {
      if (firstTimeoutRef.current) {
        clearTimeout(firstTimeoutRef.current);
      }
      if (secondTimeoutRef.current) {
        clearTimeout(secondTimeoutRef.current);
      }
      if (thirdTimeoutRef.current) {
        clearTimeout(thirdTimeoutRef.current);
      }
      if (endOfServiceAlertTimeoutRef.current) {
        clearTimeout(endOfServiceAlertTimeoutRef.current);
      }
    };
  }, [
    timeToClosingTime,
    firstWarningDelay,
    secondWarningDelay,
    thirdWarningDelay,
    store,
    addNotice,
    notices,
    storeId,
    t,
  ]);
};
